import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import type {
  ActivationCode,
  ActivationFeatureKey,
  OperationsAuditEvent,
  OperationsCustomer,
  OperationsCustomerDetail,
  OperationsCustomerStatus,
  OrganizationEntitlement
} from "@ai-sales/contracts";
import { api } from "./api.js";

type OpsSection = "customers" | "audit";
type CustomerTab = "activation" | "organizations" | "audit";

const featureLabels: Record<ActivationFeatureKey, string> = {
  assistant: "AI 助手",
  web_workspace: "独立 Web 端",
  scenario_studio: "场景工作室",
  write_actions: "受控写操作",
  knowledge: "Salesforce Knowledge"
};

const statusLabels: Record<string, string> = {
  unused: "待使用",
  used: "已使用",
  revoked: "已撤销",
  expired: "已过期",
  pending: "待生效",
  active: "正常",
  suspended: "已暂停",
  success: "成功",
  denied: "已拒绝",
  failure: "失败"
};

const customerStatusLabels: Record<OperationsCustomerStatus, string> = {
  prospect: "洽谈中",
  active: "合作中",
  suspended: "已暂停",
  closed: "已终止"
};

export function OpsApp() {
  const [authenticated, setAuthenticated] = useState(false);
  const [operatorEmail, setOperatorEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState<OpsSection>("customers");
  const [customers, setCustomers] = useState<OperationsCustomer[]>([]);
  const [auditEvents, setAuditEvents] = useState<OperationsAuditEvent[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerDetail, setCustomerDetail] = useState<OperationsCustomerDetail>();
  const [customerTab, setCustomerTab] = useState<CustomerTab>("activation");
  const [customerDrawer, setCustomerDrawer] = useState<"create" | "edit">();
  const [activationDrawerOpen, setActivationDrawerOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<OrganizationEntitlement>();
  const [revealedCode, setRevealedCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadBaseData = useCallback(async () => {
    const [customerResult, auditResult] = await Promise.all([
      api.listOperationsCustomers(),
      api.listOperationsAuditEvents()
    ]);
    setCustomers(customerResult.items);
    setAuditEvents(auditResult.items);
  }, []);

  const loadCustomerDetail = useCallback(async (customerId: string) => {
    const detail = await api.getOperationsCustomer(customerId);
    setCustomerDetail(detail);
    setCustomers((current) => current.map((customer) =>
      customer.id === detail.customer.id ? detail.customer : customer
    ));
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const session = await api.resumeSession("ops");
        const resumedOperatorEmail = session.operatorEmail?.trim().toLowerCase();
        if (session.channel !== "ops" || !resumedOperatorEmail) throw new Error("未登录");
        setOperatorEmail(resumedOperatorEmail);
        setAuthenticated(true);
        await loadBaseData();
      } catch {
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadBaseData]);

  const refresh = useCallback(async () => {
    setError("");
    try {
      await loadBaseData();
      if (selectedCustomerId) await loadCustomerDetail(selectedCustomerId);
      setNotice("数据已刷新");
    } catch (cause) {
      setError(errorMessage(cause, "数据刷新失败"));
    }
  }, [loadBaseData, loadCustomerDetail, selectedCustomerId]);

  const openCustomer = async (customerId: string) => {
    setError("");
    setSelectedCustomerId(customerId);
    setCustomerDetail(undefined);
    setCustomerTab("activation");
    try {
      await loadCustomerDetail(customerId);
    } catch (cause) {
      setError(errorMessage(cause, "客户详情加载失败"));
    }
  };

  const closeCustomer = () => {
    setSelectedCustomerId("");
    setCustomerDetail(undefined);
    setCustomerTab("activation");
  };

  if (loading) {
    return <main className="ops-auth-page"><div className="ops-auth-card ops-auth-card--loading"><OpsMark /><span className="ops-loader" /><h1>正在验证运营身份</h1></div></main>;
  }
  if (!authenticated) {
    return <OperationsLogin onAuthenticated={async (email) => {
      setOperatorEmail(email);
      setAuthenticated(true);
      setLoading(true);
      try {
        await loadBaseData();
      } finally {
        setLoading(false);
      }
    }} />;
  }

  const selectedCustomer = customerDetail?.customer;
  const customerAuditEvents = selectedCustomer ? auditEvents.filter((event) =>
    event.targetId === selectedCustomer.id || event.details.customerId === selectedCustomer.id
  ) : [];

  return (
    <main className="ops-shell">
      <aside className="ops-sidebar">
        <div className="ops-brand"><OpsMark /><div><strong>AI 助手运营中心</strong><span>产品授权管理</span></div></div>
        <nav aria-label="运营中心导航">
          <button className={section === "customers" ? "is-active" : ""} onClick={() => {
            setSection("customers");
            closeCustomer();
          }}><span>◇</span>客户管理</button>
          <button className={section === "audit" ? "is-active" : ""} onClick={() => {
            setSection("audit");
            closeCustomer();
          }}><span>◷</span>操作审计</button>
        </nav>
        <div className="ops-sidebar-note"><span className="ops-live-dot" />授权服务运行正常</div>
      </aside>

      <section className="ops-workspace">
        <header className="ops-topbar">
          <div><span className="ops-topbar-label">内部运营账号</span><strong>{operatorEmail}</strong></div>
          <div className="ops-topbar-actions">
            <button className="ops-icon-button" onClick={() => void refresh()} aria-label="刷新数据">↻</button>
            <button className="ops-text-button" onClick={async () => {
              await api.logout();
              setOperatorEmail("");
              setAuthenticated(false);
            }}>退出登录</button>
          </div>
        </header>

        <div className="ops-content">
          {notice ? <Notice tone="success" onClose={() => setNotice("")}>{notice}</Notice> : null}
          {error ? <Notice tone="error" onClose={() => setError("")}>{error}</Notice> : null}

          {section === "customers" && !selectedCustomerId ? <>
            <div className="ops-title-row">
              <div><p className="ops-eyebrow">CUSTOMER ACCESS</p><h1>客户管理</h1>
                <p>先维护客户档案，再从客户详情创建和管理产品激活码。</p></div>
              <button className="ops-primary-button" onClick={() => setCustomerDrawer("create")}>＋ 新建客户</button>
            </div>
            <CustomerListPanel customers={customers} onOpen={(id) => void openCustomer(id)} />
          </> : null}

          {section === "customers" && selectedCustomerId ? customerDetail ? <>
            <CustomerDetailHeader customer={customerDetail.customer} onBack={closeCustomer} onEdit={() => setCustomerDrawer("edit")} />
            <CustomerProfileCard customer={customerDetail.customer} />
            <CustomerActivationSteps activationCodeCount={customerDetail.activationCodes.length} organizationCount={customerDetail.organizations.length} />
            <div className="ops-detail-tabs" role="tablist" aria-label="客户详情">
              <button className={customerTab === "activation" ? "is-active" : ""} onClick={() => setCustomerTab("activation")}>
                激活码 <span>{customerDetail.activationCodes.length}</span>
              </button>
              <button className={customerTab === "organizations" ? "is-active" : ""} onClick={() => setCustomerTab("organizations")}>
                已激活组织 <span>{customerDetail.organizations.length}</span>
              </button>
              <button className={customerTab === "audit" ? "is-active" : ""} onClick={() => setCustomerTab("audit")}>
                操作记录 <span>{customerAuditEvents.length}</span>
              </button>
            </div>
            {customerTab === "activation" ? <ActivationCodePanel
              activationCodes={customerDetail.activationCodes}
              onCreate={() => setActivationDrawerOpen(true)}
              onRevoke={async (code) => {
                try {
                  await api.revokeActivationCode(code.id);
                  setNotice("激活码已撤销");
                  await loadCustomerDetail(selectedCustomerId);
                  await loadBaseData();
                } catch (cause) {
                  setError(errorMessage(cause, "撤销失败"));
                }
              }}
            /> : null}
            {customerTab === "organizations" ? <OrganizationPanel
              organizations={customerDetail.organizations}
              onEdit={setEditingOrganization}
            /> : null}
            {customerTab === "audit" ? <AuditPanel events={customerAuditEvents} embedded /> : null}
          </> : <section className="ops-panel"><div className="ops-empty"><span className="ops-loader" /><strong>正在加载客户详情</strong></div></section> : null}

          {section === "audit" ? <>
            <div className="ops-title-row"><div><p className="ops-eyebrow">AUDIT TRAIL</p><h1>操作审计</h1>
              <p>集中查看客户、激活码和组织授权的关键操作记录。</p></div></div>
            <AuditPanel events={auditEvents} />
          </> : null}

        </div>
      </section>

      {customerDrawer ? <CustomerDrawer
        customer={customerDrawer === "edit" ? selectedCustomer : undefined}
        onClose={() => setCustomerDrawer(undefined)}
        onSaved={async (customer) => {
          setCustomerDrawer(undefined);
          setNotice(customerDrawer === "edit" ? "客户档案已更新" : "客户已创建，现在可以在客户详情创建激活码");
          await loadBaseData();
          if (customerDrawer === "edit") await loadCustomerDetail(customer.id);
          else await openCustomer(customer.id);
        }}
      /> : null}
      {activationDrawerOpen && selectedCustomer ? <CreateActivationDrawer
        customer={selectedCustomer}
        onClose={() => setActivationDrawerOpen(false)}
        onCreated={async (plaintextCode) => {
          setActivationDrawerOpen(false);
          setRevealedCode(plaintextCode);
          await loadCustomerDetail(selectedCustomer.id);
          await loadBaseData();
        }}
      /> : null}
      {editingOrganization ? <OrganizationDrawer
        organization={editingOrganization}
        onClose={() => setEditingOrganization(undefined)}
        onSaved={async () => {
          setEditingOrganization(undefined);
          setNotice("组织授权已更新，新请求将立即应用最新状态");
          if (selectedCustomerId) await loadCustomerDetail(selectedCustomerId);
          await loadBaseData();
        }}
      /> : null}
      {revealedCode ? <OneTimeCodeDialog code={revealedCode} onClose={() => {
        setRevealedCode("");
        setNotice("激活码已创建；明文已从页面移除，请妥善保管已复制的内容");
      }} /> : null}
    </main>
  );
}

function OperationsLogin({ onAuthenticated }: { onAuthenticated: (email: string) => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"email" | "otp">("email");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!resendAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [resendAt]);

  const requestCode = async () => {
    setBusy(true);
    setError("");
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const response = await api.requestOperationsEmailCode(normalizedEmail);
      setEmail(normalizedEmail);
      setStep("otp");
      setResendAt(Date.now() + response.resendInSeconds * 1_000);
      setNow(Date.now());
    } catch (cause) {
      setError(errorMessage(cause, "验证码发送失败"));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (step === "email") return void requestCode();
    setBusy(true);
    setError("");
    try {
      const normalizedEmail = email.trim().toLowerCase();
      await api.verifyOperationsEmailCode(normalizedEmail, code);
      await onAuthenticated(normalizedEmail);
    } catch (cause) {
      setError(errorMessage(cause, "登录失败"));
    } finally {
      setBusy(false);
    }
  };
  const resendSeconds = Math.max(0, Math.ceil((resendAt - now) / 1_000));
  return <main className="ops-auth-page">
    <section className="ops-auth-intro">
      <p className="ops-eyebrow">AI ASSISTANT OPERATIONS</p>
      <h1>客户与产品授权，<br />在这里统一管理。</h1>
      <p>维护客户档案、签发一次性激活码，并跟踪 Salesforce Org 的产品授权状态与使用范围。</p>
      <div className="ops-auth-security"><span>✓</span><div><strong>内部运营访问</strong><p>仅授权运营账号可登录，关键操作自动写入审计记录。</p></div></div>
    </section>
    <form className="ops-auth-card" onSubmit={submit}>
      <OpsMark /><p className="ops-eyebrow">AI 助手运营中心</p>
      <h2>{step === "email" ? "运营账号登录" : "输入邮箱验证码"}</h2>
      <p>{step === "email" ? "使用指定运营邮箱完成安全验证。" : `验证码已发送至 ${maskEmail(email)}`}</p>
      {error ? <div className="ops-form-error">{error}</div> : null}
      <label>运营邮箱<input type="email" value={email} required autoComplete="email" readOnly={step === "otp"}
        onChange={(event) => setEmail(event.target.value)} placeholder="ops@example.com" /></label>
      {step === "otp" ? <label>6 位验证码<input autoFocus inputMode="numeric" autoComplete="one-time-code"
        value={code} maxLength={6} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="000000" /></label> : null}
      <button className="ops-primary-button ops-primary-button--wide" disabled={busy || (step === "otp" && code.length !== 6)}>
        {busy ? "正在验证…" : step === "email" ? "获取验证码" : "安全登录"}
      </button>
      {step === "otp" ? <button type="button" className="ops-link-button" disabled={busy || resendSeconds > 0} onClick={() => void requestCode()}>
        {resendSeconds > 0 ? `${resendSeconds} 秒后可重新发送` : "重新发送验证码"}
      </button> : null}
      <small>登录和授权操作均记录在内部审计日志中。</small>
    </form>
  </main>;
}

function CustomerListPanel({ customers, onOpen }: { customers: OperationsCustomer[]; onOpen: (id: string) => void }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const filtered = useMemo(() => customers.filter((customer) =>
    (status === "all" || customer.status === status) &&
    [customer.customerCode, customer.name, customer.primaryContactName, customer.contactEmail, customer.countryRegion]
      .join(" ").toLowerCase().includes(query.trim().toLowerCase())
  ), [customers, query, status]);
  const activeCount = customers.filter(({ status: value }) => value === "active").length;
  return <section className="ops-panel">
    <div className="ops-panel-header ops-panel-header--compact">
      <div><h2>客户档案</h2><p>共 {customers.length} 家客户，其中 {activeCount} 家合作中。</p></div>
      <div className="ops-table-tools">
        <input aria-label="搜索客户" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索客户名称、编号或联系人…" />
        <select aria-label="筛选客户状态" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">全部状态</option>
          {(Object.keys(customerStatusLabels) as OperationsCustomerStatus[]).map((value) =>
            <option key={value} value={value}>{customerStatusLabels[value]}</option>)}
        </select>
      </div>
    </div>
    <div className="ops-table-wrap"><table className="ops-table ops-customer-table"><thead><tr>
      <th>客户编号</th><th>客户名称</th><th>状态</th><th>主要联系人</th><th>国家 / 地区</th><th>合同期限</th><th>最近更新</th><th />
    </tr></thead><tbody>
      {filtered.map((customer) => <tr key={customer.id}>
        <td><code>{customer.customerCode}</code></td>
        <td><strong>{customer.name}</strong><small>{customer.contactEmail}</small></td>
        <td><CustomerStatusBadge status={customer.status} /></td>
        <td>{customer.primaryContactName}<small>{customer.contactPhone ?? "—"}</small></td>
        <td>{customer.countryRegion}</td>
        <td>{contractPeriod(customer)}</td>
        <td>{formatDateTime(customer.updatedAt)}</td>
        <td><button className="ops-row-button" onClick={() => onOpen(customer.id)}>查看详情</button></td>
      </tr>)}
      {!filtered.length ? <tr><td colSpan={8}><EmptyState title="暂无匹配客户" description="调整筛选条件，或先新建客户档案。" /></td></tr> : null}
    </tbody></table></div>
  </section>;
}

function CustomerDetailHeader({ customer, onBack, onEdit }: { customer: OperationsCustomer; onBack: () => void; onEdit: () => void }) {
  return <div className="ops-customer-heading">
    <button className="ops-back-button" onClick={onBack}>‹ 返回客户管理</button>
    <div className="ops-customer-heading-row"><div>
      <h1>{customer.name}</h1><code>{customer.customerCode}</code><CustomerStatusBadge status={customer.status} />
    </div><button className="ops-secondary-button" onClick={onEdit}>编辑客户</button></div>
  </div>;
}

function CustomerProfileCard({ customer }: { customer: OperationsCustomer }) {
  const fields = [
    ["主要联系人", customer.primaryContactName],
    ["联系邮箱", customer.contactEmail],
    ["联系电话", customer.contactPhone ?? "—"],
    ["国家 / 地区", customer.countryRegion],
    ["合同期限", contractPeriod(customer)],
    ["最近更新", formatDateTime(customer.updatedAt)],
    ["内部备注", customer.note ?? "—"]
  ];
  return <section className="ops-profile-card">{fields.map(([label, value], index) =>
    <div key={label} className={index === fields.length - 1 ? "ops-profile-note" : ""}><span>{label}</span><strong>{value}</strong></div>)}</section>;
}

function CustomerActivationSteps({ activationCodeCount, organizationCount }: {
  activationCodeCount: number;
  organizationCount: number;
}) {
  const steps = [
    { number: 1, title: "签发激活码", description: "限定 Org ID 和首位管理员 User ID", done: activationCodeCount > 0 },
    { number: 2, title: "Salesforce 内激活", description: "系统自动识别环境并核对签名上下文", done: organizationCount > 0 },
    { number: 3, title: "管理员配置产品", description: "进入管理中心分配 AI 用户和业务场景", done: organizationCount > 0 }
  ];
  return <section className="ops-activation-steps" aria-label="客户激活进度">
    {steps.map((step) => <article className={step.done ? "is-done" : ""} key={step.number}>
      <span>{step.done ? "✓" : step.number}</span><div><strong>{step.title}</strong><p>{step.description}</p></div>
    </article>)}
  </section>;
}

function ActivationCodePanel({ activationCodes, onCreate, onRevoke }: {
  activationCodes: ActivationCode[];
  onCreate: () => void;
  onRevoke: (activationCode: ActivationCode) => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const filtered = useMemo(() => activationCodes.filter((code) =>
    (status === "all" || code.status === status) &&
    [code.preview, code.expectedOrganizationId, code.expectedSalesforceUserId, code.note]
      .filter(Boolean).join(" ").toLowerCase().includes(query.trim().toLowerCase())
  ), [activationCodes, query, status]);
  return <section className="ops-panel">
    <div className="ops-panel-header ops-panel-header--compact">
      <div><h2>激活码</h2><p>一个客户可拥有多个激活码；每个激活码只能成功使用一次。</p></div>
      <div className="ops-table-tools">
        <button className="ops-primary-button" onClick={onCreate}>＋ 创建激活码</button>
        <select aria-label="筛选激活码状态" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">全部状态</option><option value="unused">待使用</option><option value="used">已使用</option><option value="expired">已过期</option><option value="revoked">已撤销</option>
        </select>
        <input aria-label="搜索激活码" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索激活码、Org ID…" />
      </div>
    </div>
    <div className="ops-table-wrap"><table className="ops-table"><thead><tr>
      <th>激活码</th><th>状态</th><th>适用环境</th><th>席位 / 功能范围</th><th>有效期</th><th>已绑定 Org</th><th>创建时间</th><th />
    </tr></thead><tbody>
      {filtered.map((code) => <tr key={code.id}>
        <td><code>{code.preview}</code><small>{code.note || "—"}</small></td>
        <td><StatusBadge status={code.status} /></td>
        <td>{code.environment === "auto_detect" ? "Salesforce 自动识别" : code.environment}</td>
        <td><strong>{code.seatLimit} 席</strong><small>{code.featureKeys.map((key) => featureLabels[key]).join("、")}</small></td>
        <td>{formatDate(code.expiresAt)}<small>{relativeExpiry(code.expiresAt)}</small></td>
        <td><code>{code.boundOrganizationId ?? code.expectedOrganizationId ?? "未限定"}</code></td>
        <td>{formatDateTime(code.createdAt)}</td>
        <td>{code.status === "unused" ? <button className="ops-row-button ops-row-button--danger" onClick={() => void onRevoke(code)}>撤销</button> : null}</td>
      </tr>)}
      {!filtered.length ? <tr><td colSpan={8}><EmptyState title="暂无激活码" description="为当前客户创建第一个产品激活码。" /></td></tr> : null}
    </tbody></table></div>
  </section>;
}

function OrganizationPanel({ organizations, onEdit }: { organizations: OrganizationEntitlement[]; onEdit: (organization: OrganizationEntitlement) => void }) {
  return <section className="ops-panel">
    <div className="ops-panel-header ops-panel-header--compact"><div><h2>已激活组织</h2><p>客户管理员使用激活码成功授权后，Salesforce Org 会显示在这里。</p></div></div>
    <div className="ops-table-wrap"><table className="ops-table"><thead><tr>
      <th>组织</th><th>环境</th><th>授权状态</th><th>席位</th><th>功能范围</th><th>到期时间</th><th />
    </tr></thead><tbody>
      {organizations.map((organization) => <tr key={organization.organizationId}>
        <td><strong>{organization.displayName}</strong><code>{organization.organizationId}</code></td>
        <td>{organization.environment.startsWith("sfoa_") ? "Salesforce on Alibaba Cloud" : "Salesforce Global"}{organization.environment.endsWith("_sandbox") ? " · 沙盒" : " · 正式环境"}</td><td><StatusBadge status={organization.status} /></td>
        <td>{organization.seatLimit}</td><td><small>{organization.featureKeys.map((key) => featureLabels[key]).join("、")}</small></td>
        <td>{organization.expiresAt ? formatDate(organization.expiresAt) : "长期有效"}</td>
        <td><button className="ops-row-button" onClick={() => onEdit(organization)}>管理</button></td>
      </tr>)}
      {!organizations.length ? <tr><td colSpan={7}><EmptyState title="尚无已激活组织" description="客户完成首次 Salesforce 授权后会出现在这里。" /></td></tr> : null}
    </tbody></table></div>
  </section>;
}

function AuditPanel({ events, embedded = false }: { events: OperationsAuditEvent[]; embedded?: boolean }) {
  return <section className="ops-panel">
    <div className="ops-panel-header ops-panel-header--compact"><div><h2>{embedded ? "客户操作记录" : "操作审计"}</h2>
      <p>记录关键控制结果和必要标识，不保存验证码、Token、Secret 或激活码明文。</p></div></div>
    <div className="ops-table-wrap"><table className="ops-table"><thead><tr>
      <th>时间</th><th>操作人</th><th>操作</th><th>目标</th><th>结果</th><th>变更摘要</th>
    </tr></thead><tbody>
      {events.map((event) => <tr key={event.id}>
        <td>{formatDateTime(event.createdAt)}</td><td>{event.actorEmail}</td><td><code>{event.action}</code></td>
        <td>{event.targetType}<small>{event.targetId}</small></td><td><StatusBadge status={event.outcome} /></td>
        <td><small>{auditSummary(event.details)}</small></td>
      </tr>)}
      {!events.length ? <tr><td colSpan={6}><EmptyState title="暂无操作记录" description="客户、激活码和组织授权变更会自动记录。" /></td></tr> : null}
    </tbody></table></div>
  </section>;
}

function CustomerDrawer({ customer, onClose, onSaved }: {
  customer?: OperationsCustomer;
  onClose: () => void;
  onSaved: (customer: OperationsCustomer) => Promise<void>;
}) {
  const [name, setName] = useState(customer?.name ?? "");
  const [status, setStatus] = useState<OperationsCustomerStatus>(customer?.status ?? "active");
  const [primaryContactName, setPrimaryContactName] = useState(customer?.primaryContactName ?? "");
  const [contactEmail, setContactEmail] = useState(customer?.contactEmail ?? "");
  const [contactPhone, setContactPhone] = useState(customer?.contactPhone ?? "");
  const [countryRegion, setCountryRegion] = useState(customer?.countryRegion ?? "中国");
  const [address, setAddress] = useState(customer?.address ?? "");
  const [contractStartDate, setContractStartDate] = useState(customer?.contractStartDate ?? "");
  const [contractEndDate, setContractEndDate] = useState(customer?.contractEndDate ?? "");
  const [note, setNote] = useState(customer?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const input = {
      name: name.trim(), status, primaryContactName: primaryContactName.trim(), contactEmail: contactEmail.trim(),
      ...(contactPhone.trim() ? { contactPhone: contactPhone.trim() } : {}),
      countryRegion: countryRegion.trim(),
      ...(address.trim() ? { address: address.trim() } : {}),
      ...(contractStartDate ? { contractStartDate } : {}),
      ...(contractEndDate ? { contractEndDate } : {}),
      ...(note.trim() ? { note: note.trim() } : {})
    };
    try {
      const saved = customer
        ? await api.updateOperationsCustomer(customer.id, { ...input, expectedVersion: customer.version })
        : await api.createOperationsCustomer(input);
      await onSaved(saved);
    } catch (cause) {
      setError(errorMessage(cause, customer ? "客户档案更新失败" : "客户创建失败"));
    } finally {
      setBusy(false);
    }
  };
  return <Drawer title={customer ? "编辑客户" : "新建客户"} description={customer ? `${customer.customerCode} · ${customer.name}` : "维护客户基础信息后，才能创建产品激活码。"} onClose={onClose}>
    <form className="ops-drawer-form" onSubmit={submit}>
      {error ? <div className="ops-form-error">{error}</div> : null}
      <label>客户名称<input value={name} onChange={(event) => setName(event.target.value)} maxLength={160} required placeholder="请输入企业或组织名称" /></label>
      <div className="ops-form-row"><label>客户编号<input value={customer?.customerCode ?? "保存后自动生成"} readOnly /></label>
        <label>客户状态<select value={status} onChange={(event) => setStatus(event.target.value as OperationsCustomerStatus)}>
          {(Object.keys(customerStatusLabels) as OperationsCustomerStatus[]).map((value) => <option key={value} value={value}>{customerStatusLabels[value]}</option>)}
        </select></label></div>
      <div className="ops-form-row"><label>主要联系人<input value={primaryContactName} onChange={(event) => setPrimaryContactName(event.target.value)} required /></label>
        <label>联系邮箱<input type="email" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} required /></label></div>
      <div className="ops-form-row"><label>联系电话<input value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} /></label>
        <label>国家 / 地区<input value={countryRegion} onChange={(event) => setCountryRegion(event.target.value)} required /></label></div>
      <label>联系地址<input value={address} onChange={(event) => setAddress(event.target.value)} maxLength={300} /></label>
      <div className="ops-form-row"><label>合同开始日期<input type="date" value={contractStartDate} onChange={(event) => setContractStartDate(event.target.value)} /></label>
        <label>合同结束日期<input type="date" min={contractStartDate || undefined} value={contractEndDate} onChange={(event) => setContractEndDate(event.target.value)} /></label></div>
      <label>内部备注<textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} placeholder="合同、交付或客户协作说明" /></label>
      <div className="ops-impact-note"><strong>客户档案是授权前置条件</strong><p>激活码、已激活 Org 和相关操作记录都会归集到该客户。</p></div>
      <div className="ops-drawer-actions"><button type="button" className="ops-secondary-button" onClick={onClose}>取消</button>
        <button className="ops-primary-button" disabled={busy}>{busy ? "正在保存…" : customer ? "保存客户" : "创建客户"}</button></div>
    </form>
  </Drawer>;
}

function CreateActivationDrawer({ customer, onClose, onCreated }: {
  customer: OperationsCustomer;
  onClose: () => void;
  onCreated: (plaintextCode: string) => Promise<void>;
}) {
  const initialExpiry = new Date();
  initialExpiry.setFullYear(initialExpiry.getFullYear() + 1);
  const [expiresAt, setExpiresAt] = useState(initialExpiry.toISOString().slice(0, 16));
  const [seatLimit, setSeatLimit] = useState(20);
  const [featureKeys, setFeatureKeys] = useState<ActivationFeatureKey[]>(["assistant", "web_workspace", "scenario_studio", "write_actions"]);
  const [expectedOrganizationId, setExpectedOrganizationId] = useState("");
  const [expectedSalesforceUserId, setExpectedSalesforceUserId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api.createActivationCode(customer.id, {
        expiresAt: new Date(expiresAt).toISOString(),
        seatLimit,
        featureKeys,
        expectedOrganizationId: expectedOrganizationId.trim(),
        expectedSalesforceUserId: expectedSalesforceUserId.trim(),
        ...(note.trim() ? { note: note.trim() } : {})
      });
      await onCreated(result.plaintextCode);
    } catch (cause) {
      setError(errorMessage(cause, "激活码创建失败"));
    } finally {
      setBusy(false);
    }
  };
  return <Drawer title="创建激活码" description={`客户：${customer.name}`} onClose={onClose}>
    <form className="ops-drawer-form" onSubmit={submit}>
      {error ? <div className="ops-form-error">{error}</div> : null}
      <div className="ops-impact-note"><strong>一次性产品授权</strong><p>客户管理员必须从已安装产品包的 Salesforce Org 内发起。系统自动识别 Global / SFoA 与正式 / 沙盒环境，并在 OAuth 回调后再次核对身份。</p></div>
      <div className="ops-form-row"><label>有效期至<input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} required /></label>
        <label>AI 用户席位<input type="number" min={1} max={10000} value={seatLimit} onChange={(event) => setSeatLimit(Number(event.target.value))} required /></label></div>
      <fieldset><legend>功能范围</legend><div className="ops-check-grid">{(Object.keys(featureLabels) as ActivationFeatureKey[]).map((key) =>
        <label key={key}><input type="checkbox" checked={featureKeys.includes(key)} onChange={(event) => setFeatureKeys((current) =>
          event.target.checked ? [...new Set([...current, key])] : current.filter((value) => value !== key)
        )} />{featureLabels[key]}</label>)}</div></fieldset>
      <label>目标 Salesforce Org ID<input aria-label="目标 Salesforce Org ID" required minLength={15} pattern="00D[A-Za-z0-9]{12}([A-Za-z0-9]{3})?" value={expectedOrganizationId} onChange={(event) => setExpectedOrganizationId(event.target.value.trim())} placeholder="00Dxxxxxxxxxxxxxxx" maxLength={18} /><small>必填。仅该 Org 可以使用此激活码，授权到其他 Org 将被拒绝。</small></label>
      <label>首位管理员 Salesforce User ID<input aria-label="首位管理员 Salesforce User ID" required minLength={15} pattern="005[A-Za-z0-9]{12}([A-Za-z0-9]{3})?" value={expectedSalesforceUserId} onChange={(event) => setExpectedSalesforceUserId(event.target.value.trim())} placeholder="005xxxxxxxxxxxxxxx" maxLength={18} /><small>必填。只有该用户能完成首次激活，成功后会成为 AI 平台管理员。</small></label>
      <label>内部备注<textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} placeholder="合同、交付批次或授权用途" /></label>
      <div className="ops-drawer-actions"><button type="button" className="ops-secondary-button" onClick={onClose}>取消</button>
        <button className="ops-primary-button" disabled={busy || !featureKeys.length}>{busy ? "正在创建…" : "创建激活码"}</button></div>
    </form>
  </Drawer>;
}

function OrganizationDrawer({ organization, onClose, onSaved }: {
  organization: OrganizationEntitlement;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [status, setStatus] = useState(organization.status);
  const [seatLimit, setSeatLimit] = useState(organization.seatLimit);
  const [featureKeys, setFeatureKeys] = useState<ActivationFeatureKey[]>([...organization.featureKeys]);
  const [expiresAt, setExpiresAt] = useState(organization.expiresAt?.slice(0, 16) ?? "");
  const [suspensionReason, setSuspensionReason] = useState(organization.suspensionReason ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.updateOrganizationEntitlement(organization.organizationId, {
        status, seatLimit, featureKeys,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        suspensionReason: suspensionReason.trim() || null,
        expectedVersion: organization.version
      });
      await onSaved();
    } catch (cause) {
      setError(errorMessage(cause, "组织授权更新失败"));
    } finally {
      setBusy(false);
    }
  };
  return <Drawer title="管理组织授权" description={`${organization.displayName} · ${organization.organizationId}`} onClose={onClose}>
    <form className="ops-drawer-form" onSubmit={submit}>
      {error ? <div className="ops-form-error">{error}</div> : null}
      <label>授权状态<select value={status} onChange={(event) => setStatus(event.target.value as OrganizationEntitlement["status"])}>
        <option value="active">正常</option><option value="suspended">暂停</option><option value="expired">过期</option><option value="revoked">撤销</option><option value="pending">待生效</option>
      </select></label>
      <div className="ops-form-row"><label>AI 用户席位<input type="number" min={1} max={10000} value={seatLimit} onChange={(event) => setSeatLimit(Number(event.target.value))} /></label>
        <label>到期时间<input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} /></label></div>
      <fieldset><legend>功能范围</legend><div className="ops-check-grid">{(Object.keys(featureLabels) as ActivationFeatureKey[]).map((key) =>
        <label key={key}><input type="checkbox" checked={featureKeys.includes(key)} onChange={(event) => setFeatureKeys((current) =>
          event.target.checked ? [...new Set([...current, key])] : current.filter((value) => value !== key)
        )} />{featureLabels[key]}</label>)}</div></fieldset>
      <label>暂停或变更说明<textarea value={suspensionReason} onChange={(event) => setSuspensionReason(event.target.value)} maxLength={500} /></label>
      <div className="ops-impact-note"><strong>立即生效</strong><p>保存后，所有新会话和请求都会重新检查该 Org 的授权状态。</p></div>
      <div className="ops-drawer-actions"><button type="button" className="ops-secondary-button" onClick={onClose}>取消</button>
        <button className="ops-primary-button" disabled={busy || !featureKeys.length}>{busy ? "正在保存…" : "保存授权"}</button></div>
    </form>
  </Drawer>;
}

function OneTimeCodeDialog({ code, onClose }: { code: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return <div className="ops-dialog-backdrop" role="presentation"><section className="ops-code-dialog" role="dialog" aria-modal="true" aria-labelledby="one-time-code-title">
    <span className="ops-success-mark">✓</span><p className="ops-eyebrow">CREATED SECURELY</p><h2 id="one-time-code-title">激活码创建成功</h2>
    <p>这是唯一一次显示完整激活码。关闭后系统无法再次还原。</p><code>{code}</code>
    <button className="ops-primary-button ops-primary-button--wide" onClick={async () => {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    }}>{copied ? "已复制到剪贴板" : "复制激活码"}</button>
    <button className="ops-link-button" onClick={onClose}>我已安全保存，关闭</button>
  </section></div>;
}

function Drawer({ title, description, onClose, children }: { title: string; description: string; onClose: () => void; children: ReactNode }) {
  return <div className="ops-drawer-backdrop" onMouseDown={(event) => {
    if (event.target === event.currentTarget) onClose();
  }}><aside className="ops-drawer" aria-label={title}><header><div><p className="ops-eyebrow">OPERATIONS</p><h2>{title}</h2><p>{description}</p></div>
    <button onClick={onClose} aria-label="关闭">×</button></header>{children}</aside></div>;
}

function Notice({ tone, onClose, children }: { tone: "success" | "error"; onClose: () => void; children: ReactNode }) {
  return <div className={`ops-notice ops-notice--${tone}`}>{children}<button onClick={onClose}>×</button></div>;
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`ops-status ops-status--${status}`}>{statusLabels[status] ?? status}</span>;
}

function CustomerStatusBadge({ status }: { status: OperationsCustomerStatus }) {
  return <span className={`ops-status ops-status--customer-${status}`}>{customerStatusLabels[status]}</span>;
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="ops-empty"><span>◇</span><strong>{title}</strong><p>{description}</p></div>;
}

function OpsMark() {
  return <span className="ops-mark" aria-hidden="true"><i /><i /><i /></span>;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(value));
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function relativeExpiry(value: string) {
  const days = Math.ceil((Date.parse(value) - Date.now()) / (24 * 60 * 60_000));
  if (days < 0) return `已过期 ${Math.abs(days)} 天`;
  if (days === 0) return "今天到期";
  return `${days} 天后到期`;
}

function contractPeriod(customer: OperationsCustomer) {
  if (customer.contractStartDate && customer.contractEndDate) return `${customer.contractStartDate} 至 ${customer.contractEndDate}`;
  if (customer.contractStartDate) return `${customer.contractStartDate} 起`;
  if (customer.contractEndDate) return `${customer.contractEndDate} 到期`;
  return "未设置";
}

function maskEmail(email: string) {
  const [local = "", domain = ""] = email.split("@");
  return `${local.slice(0, 3)}***@${domain}`;
}

function auditSummary(details: Record<string, unknown>) {
  const entries = Object.entries(details).filter(([key]) => key !== "customerId").slice(0, 4);
  if (!entries.length) return "—";
  return entries.map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join("、") : String(value)}`).join(" · ");
}

function errorMessage(cause: unknown, fallback: string) {
  return cause instanceof Error ? cause.message : fallback;
}
